import React, { useEffect, useState } from 'react'
import { API_BASE_URL } from '../data'
import { Area, User } from '../data/types'
import { CheckIcon } from '../icons'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    isOpen: boolean
    onClose: () => void
    user: User | null
}

type AreaWithEvent = Area & { event?: { id: number; name: string } }

export const AreaAccessModal = (props: Props) => {
    const { isOpen, onClose, user } = props

    const [allAreas, setAllAreas] = useState<AreaWithEvent[]>([])
    const [accessibleAreaIds, setAccessibleAreaIds] = useState<number[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [expandedEvents, setExpandedEvents] = useState<number[]>([])

    // Fetch all areas and user's current access when modal opens
    useEffect(() => {
        if (!isOpen || !user) return

        const fetchData = async () => {
            setLoading(true)
            setError(null)

            try {
                // Fetch all events with their areas
                const eventsResponse = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (eventsResponse.ok) {
                    const result = await eventsResponse.json()
                    let eventData = result.value || result
                    if (Array.isArray(eventData) && eventData.length > 0 && Array.isArray(eventData[0])) {
                        eventData = eventData[0]
                    }

                    // Fetch areas for each event
                    const areasWithEvents: AreaWithEvent[] = []
                    for (const event of eventData) {
                        const areasResponse = await fetch(`${API_BASE_URL}/area/${event.id}`, { credentials: 'include' })
                        if (areasResponse.ok) {
                            const areasResult = await areasResponse.json()
                            let areasData = areasResult.value || areasResult
                            if (Array.isArray(areasData) && areasData.length > 0 && Array.isArray(areasData[0])) {
                                areasData = areasData[0]
                            }
                            for (const area of areasData) {
                                areasWithEvents.push({
                                    ...area,
                                    event: { id: event.id, name: event.name }
                                })
                            }
                        }
                    }
                    setAllAreas(areasWithEvents)
                }

                // Fetch user's current area access
                if (user.volunteer_csid) {
                    const accessResponse = await fetch(`${API_BASE_URL}/area-access/volunteer/${user.volunteer_csid}/areas`, { credentials: 'include' })
                    if (accessResponse.ok) {
                        const accessAreas = await accessResponse.json()
                        setAccessibleAreaIds(accessAreas.map((a: Area) => a.id))
                    }
                }
            } catch (err) {
                setError('Failed to load data')
                console.error('Error fetching data:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [isOpen, user])

    const handleToggleArea = (areaId: number) => {
        setAccessibleAreaIds(prev => {
            if (prev.includes(areaId)) {
                return prev.filter(id => id !== areaId)
            } else {
                return [...prev, areaId]
            }
        })
    }

    const handleToggleEvent = (eventId: number) => {
        setExpandedEvents(prev => {
            if (prev.includes(eventId)) {
                return prev.filter(id => id !== eventId)
            } else {
                return [...prev, eventId]
            }
        })
    }

    const handleSelectAllInEvent = (eventId: number) => {
        const eventAreaIds = allAreas
            .filter(a => a.event?.id === eventId)
            .map(a => a.id)
        setAccessibleAreaIds(prev => {
            const newIds = new Set([...prev, ...eventAreaIds])
            return Array.from(newIds)
        })
    }

    const handleSelectNoneInEvent = (eventId: number) => {
        const eventAreaIds = allAreas
            .filter(a => a.event?.id === eventId)
            .map(a => a.id)
        setAccessibleAreaIds(prev => prev.filter(id => !eventAreaIds.includes(id)))
    }

    const handleSelectAll = () => {
        setAccessibleAreaIds(allAreas.map(a => a.id))
    }

    const handleSelectNone = () => {
        setAccessibleAreaIds([])
    }

    const handleSave = async () => {
        if (!user?.volunteer_csid) {
            setError('User does not have a volunteer profile')
            return
        }

        setSaving(true)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/area-access/volunteer/${user.volunteer_csid}/areas`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ area_ids: accessibleAreaIds })
            })

            if (!response.ok) {
                throw new Error('Failed to save access')
            }

            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save access')
        } finally {
            setSaving(false)
        }
    }

    const handleClose = () => {
        setError(null)
        onClose()
    }

    // Group areas by event and sort alphabetically
    const areasByEvent = allAreas.reduce((acc, area) => {
        const eventId = area.event?.id || 0
        const eventName = area.event?.name || 'No Event'
        if (!acc[eventId]) {
            acc[eventId] = { name: eventName, areas: [] }
        }
        acc[eventId].areas.push(area)
        return acc
    }, {} as Record<number, { name: string; areas: AreaWithEvent[] }>)

    // Sort areas within each event alphabetically
    Object.values(areasByEvent).forEach(eventGroup => {
        eventGroup.areas.sort((a, b) => a.name.localeCompare(b.name))
    })

    // Get sorted event entries (by event name)
    const sortedEventEntries = Object.entries(areasByEvent).sort((a, b) =>
        a[1].name.localeCompare(b[1].name)
    )

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Manage Area Access`}
            maxWidth="lg"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={handleClose}
                        disabled={saving}
                        variant="outline"
                        color="neutral"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || loading || !user?.volunteer_csid}
                    >
                        {saving ? 'Saving...' : 'Save Access'}
                    </Button>
                </div>
            }
        >
            <div className="px-6 py-4">
                {user && (
                    <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>User:</strong> {user.name} ({user.email})
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>Role:</strong> {user.role}
                        </p>
                    </div>
                )}

                {!user?.volunteer_csid && (
                    <div className="mb-4 p-3 bg-warn-100 dark:bg-warn-900/30 border border-warn-200 dark:border-warn-700 rounded-lg">
                        <p className="text-sm text-warn-700 dark:text-warn-300">
                            <strong>Note:</strong> This user does not have a linked volunteer profile.
                            Area access cannot be granted without a volunteer profile.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-3 bg-danger-100 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-700 rounded-lg">
                        <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
                    </div>
                )}

                <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Select which areas this user can manage (accept/reject allocations, manage shifts).
                        This is in addition to any lead/deputy assignments.
                    </p>

                    <div className="flex gap-2 mb-3">
                        <Button variant="outline" size="sm" onClick={handleSelectAll} disabled={loading}>
                            Select All
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleSelectNone} disabled={loading}>
                            Select None
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading areas...</div>
                ) : sortedEventEntries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No areas found</div>
                ) : (
                    <div className="max-h-[400px] overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                        {sortedEventEntries.map(([eventIdStr, { name: eventName, areas }]) => {
                            const eventId = parseInt(eventIdStr)
                            const isExpanded = expandedEvents.includes(eventId)
                            const selectedCount = areas.filter(a => accessibleAreaIds.includes(a.id)).length

                            return (
                                <div key={eventId} className="border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                                    {/* Event Header */}
                                    <div
                                        onClick={() => handleToggleEvent(eventId)}
                                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-750"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                                ▶
                                            </span>
                                            <span className="font-medium text-gray-900 dark:text-gray-100">
                                                {eventName}
                                            </span>
                                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                                ({selectedCount}/{areas.length} selected)
                                            </span>
                                        </div>
                                        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                                            <Button variant="outline" size="sm" onClick={() => handleSelectAllInEvent(eventId)}>
                                                All
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={() => handleSelectNoneInEvent(eventId)}>
                                                None
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Areas List */}
                                    {isExpanded && areas.map(area => {
                                        const isSelected = accessibleAreaIds.includes(area.id)
                                        return (
                                            <div
                                                key={area.id}
                                                onClick={() => handleToggleArea(area.id)}
                                                className={`flex items-center justify-between p-3 ps-8 border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                                }`}
                                            >
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-900 dark:text-gray-100">
                                                        {area.name}
                                                    </p>
                                                    {(area.lead || area.deputy_lead) && (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            {area.lead && `Lead: ${area.lead.first_name} ${area.lead.last_name}`}
                                                            {area.lead && area.deputy_lead && ' | '}
                                                            {area.deputy_lead && `Deputy: ${area.deputy_lead.first_name} ${area.deputy_lead.last_name}`}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                    isSelected
                                                        ? 'bg-primary-500 text-white'
                                                        : 'bg-gray-200 dark:bg-gray-600'
                                                }`}>
                                                    {isSelected && <CheckIcon className="w-4 h-4" />}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                )}

                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    {accessibleAreaIds.length} area{accessibleAreaIds.length !== 1 ? 's' : ''} selected
                </div>
            </div>
        </Modal>
    )
}
